import { useEffect, useCallback } from "react";

import {
  fetchProfitMarginData,
  fetchRecentSales,
  fetchTopProducts,
  fetchAdminDashboardOverview,
  fetchLowStockProducts,
} from "../slices/dashboardSlice";
import { useAppDispatch, useAppSelector } from "../hooks";

const POLL_INTERVAL = 30_000; // 30 seconds

// Survives React Router navigations (component unmount/remount) within the
// same browser session so skeletons are never shown after the first load.
const sessionInitialized = {
  overview: false,
  profitMargin: false,
  recentSales: false,
  topProducts: false,
  lowStockProducts: false,
};

export function useAdminDashboard(autoLoad = true) {
  const dispatch = useAppDispatch();

  const {
    profitMarginData,
    profitMarginDataLoading,
    profitMarginInitialized,
    recentSales,
    recentSalesLoading,
    recentSalesInitialized,
    topProducts,
    topProductsLoading,
    topProductsInitialized,
    adminDashboardOverview,
    adminDashboardOverviewLoading,
    overviewInitialized,
    lowStockProducts,
    lowStockProductsLoading,
    lowStockInitialized,
    error,
  } = useAppSelector((state) => state.dashboard);

  /**
   * Load all dashboard data in parallel
   */
  const loadDashboard = useCallback(() => {
    dispatch(fetchAdminDashboardOverview());
    dispatch(fetchProfitMarginData());
    dispatch(fetchRecentSales());
    dispatch(fetchTopProducts());
    dispatch(fetchLowStockProducts());
  }, [dispatch]);

  /**
   * Load once on mount
   */
  useEffect(() => {
    if (autoLoad) {
      loadDashboard();
    }
  }, [autoLoad, loadDashboard]);

  /**
   * Poll in the background after the initial load — no loading animations shown
   */
  useEffect(() => {
    if (!autoLoad) return;
    const id = setInterval(() => {
      if (navigator.onLine) {
        loadDashboard();
      }
    }, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [autoLoad, loadDashboard]);

  // Sync Redux initialized flags into the module-level var so the "no skeleton"
  // state survives navigation (Redux resets on unmount; the module var does not).
  if (overviewInitialized) sessionInitialized.overview = true;
  if (profitMarginInitialized) sessionInitialized.profitMargin = true;
  if (recentSalesInitialized) sessionInitialized.recentSales = true;
  if (topProductsInitialized) sessionInitialized.topProducts = true;
  if (lowStockInitialized) sessionInitialized.lowStockProducts = true;

  return {
    // Data
    overview: adminDashboardOverview,
    profitMarginData,
    recentSales,
    topProducts,
    lowStockProducts,

    // Loading states (true while a request is in flight)
    loading: {
      overview: adminDashboardOverviewLoading,
      profitMargin: profitMarginDataLoading,
      recentSales: recentSalesLoading,
      topProducts: topProductsLoading,
      lowStockProducts: lowStockProductsLoading,
      any:
        adminDashboardOverviewLoading ||
        profitMarginDataLoading ||
        recentSalesLoading ||
        topProductsLoading ||
        lowStockProductsLoading,
    },

    // Whether each section has completed its first fetch (used to suppress
    // skeleton UIs during background polling refreshes and re-navigation).
    initialized: {
      overview: overviewInitialized || sessionInitialized.overview,
      profitMargin: profitMarginInitialized || sessionInitialized.profitMargin,
      recentSales: recentSalesInitialized || sessionInitialized.recentSales,
      topProducts: topProductsInitialized || sessionInitialized.topProducts,
      lowStockProducts: lowStockInitialized || sessionInitialized.lowStockProducts,
    },

    // Errors
    error,

    // Actions
    reload: loadDashboard,
  };
}
